import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { supabase } from '../../../lib/supabase';
import { formatNotificationText } from '../../../utils/notificationsService';
import { getNotificationIcon } from '../utils/getNotificationIcon';
import { NotificationIconGlyph } from '../NotificationIcons';
import './NotificationToastListener.css';

/**
 * Komponent nasłuchujący nowych powiadomień w czasie rzeczywistym
 * i wyświetlający tostery
 */
function NotificationToastListener({ userId }) {
  const { t } = useTranslation('notifications');
  const channelRef = useRef(null);

  useEffect(() => {
    if (!userId) return;

    // Subskrybuj zmiany w powiadomieniach
    const notificationsChannel = supabase
      .channel(`notification_toasts_${userId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, async (payload) => {
        const newNotification = payload.new;
        
        // Pobierz pełne dane powiadomienia (w tym relacje z event)
        try {
          const { data, error } = await supabase
            .from('notifications')
            .select(`
              *,
              event:events(
                id,
                title,
                host_id
              )
            `)
            .eq('id', newNotification.id)
            .single();

          if (error) {
            console.error('Error fetching notification details:', error);
            return;
          }

          const notification = data;
          const icon = getNotificationIcon(notification.type);
          const message = formatNotificationText(notification, t);

          // Wyświetl toster
          toast(
            <div className="notification-toast-content">
              <span className={`notification-toast-icon notification-toast-icon--${icon.group}`}>
                <NotificationIconGlyph icon={icon.icon} className="notification-toast-icon-svg" size={35} />
              </span>
              <span className="notification-toast-message">{message}</span>
            </div>,
            {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              className: `notification-toast-shell notification-toast-shell--${icon.group}`,
              progressClassName: `notification-toast-progress notification-toast-progress--${icon.group}`,
            }
          );

          // Emituj event żeby zaktualizować licznik w menu
          window.dispatchEvent(new CustomEvent('notificationsChanged', {
            detail: { userId, payload }
          }));
        } catch (err) {
          console.error('Error in notification toast handler:', err);
        }
      })
      .subscribe();

    channelRef.current = notificationsChannel;

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
      }
    };
  }, [userId, t]);

  // Komponent nie renderuje niczego
  return null;
}

export default NotificationToastListener;
